import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  deleteRecipeQualitySpecification,
  deleteRecipeWorkInstruction,
  getList,
  getListById,
  patchRecipeWorkInstructionReorder,
  postRecipe,
  postRecipeQualitySpecification,
  postRecipeWorkInstruction,
} from "../../services/services";
import { ENDPOINT } from "../../services/api-endpoint";
import {
  buildDisplayWorkflowOps,
  countCriticalTargets,
  CRITICAL_RECIPE_PARAM_KEYS,
  isParamCriticalMarked,
  resolveRecipeGroupsForStep,
  validateRecipeTnmOrdering,
  type RecipeParamDef,
  type WoRowLite,
} from "../../lib/recipe-groups";
import { recipeGroupsFromPayloadLayouts } from "../../lib/recipe-process-parameters";
import "./trurecipe-pages.scss";

type RecipeDetailPayload = {
  cells?: Record<string, string>;
  workflowOperationIds?: number[];
  materialGradeIds?: number[];
  stepLayouts?: Array<{ groups: Array<{ title: string; params: RecipeParamDef[] }> }>;
  submittedForReview?: boolean;
  submittedForApproval?: boolean;
};

type RecipeDetail = {
  id: number;
  name: string;
  status: string;
  workflowId: number;
  workflowname?: string | null;
  partname?: string | null;
  materialCategoryId?: number | null;
  materialGradeId?: number | null;
  materialCategoryName?: string | null;
  materialGradeNames?: string[];
  materialGradeIds?: number[];
  payload?: RecipeDetailPayload | null;
  cardTitle?: string | null;
  paramCount?: number;
  criticalCount?: number;
  createdate?: unknown;
  updatedate?: unknown;
  createdby?: string | null;
};

type WfOp = WoRowLite & {
  machineName?: string | null;
};

type WorkflowDetail = {
  id: number;
  workflowname?: string | null;
  workflowoperations?: WfOp[];
};

function formatMesDate(d: unknown): string {
  if (d == null || d === "") return "—";
  const t = new Date(String(d)).getTime();
  if (!Number.isFinite(t)) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(t);
  } catch {
    return "—";
  }
}

/** Title-case ALL-CAPS group labels from `recipe-groups` for detail headings. */
function recipeGroupDisplayTitle(title: string): string {
  const s = title.trim();
  if (!s) return s;
  return s.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
}

type WiInstructionRow = {
  id: number;
  recipeId: number;
  sortOrder: number;
  title: string;
  description?: string | null;
  safetyNotes?: string | null;
  estimatedTime?: string | null;
};

type QsRow = {
  id: number;
  recipeId: number;
  sortOrder: number;
  spec: string;
  method?: string | null;
  frequency?: string | null;
  acceptance?: string | null;
};

function normalizeSaveStatus(s: string): "draft" | "approved" | "pending" {
  const t = s.trim().toLowerCase();
  if (t === "approved") return "approved";
  if (t === "pending" || t === "in review" || t === "in_review" || t === "in-review") {
    return "pending";
  }
  return "draft";
}

function paramSourceLabel(paramKey: string): string {
  if (paramKey === "spindle" || paramKey === "feed") return "Material Adjusted";
  if (paramKey === "cp") return "Machine Constrained";
  return "Technology Default";
}

export default function TrurecipeRecipeDetail() {
  const { recipeId } = useParams();
  const [recipe, setRecipe] = useState<RecipeDetail | null>(null);
  const [wf, setWf] = useState<WorkflowDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [cellDraft, setCellDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    const id = Number(recipeId);
    if (!Number.isFinite(id) || id <= 0) {
      setErr("Invalid recipe id");
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setErr(null);
      setWiRows([]);
      setQsRows([]);
      try {
        const raw = await getListById(ENDPOINT.RECIPE_LIST_BY_ID, id);
        if (cancelled) return;
        if (!raw || typeof raw !== "object") {
          setRecipe(null);
          setCellDraft({});
          setErr("Recipe not found.");
          setWf(null);
          return;
        }
        const r = raw as RecipeDetail;
        setRecipe(r);
        setCellDraft({ ...((r.payload?.cells ?? {}) as Record<string, string>) });
        const wfId = Number(r.workflowId);
        if (Number.isFinite(wfId) && wfId > 0) {
          const wfRaw = await getListById(ENDPOINT.WORKFLOWS_LIST_BY_ID, wfId);
          if (cancelled) return;
          const unwrapped = Array.isArray(wfRaw) ? wfRaw[0] : wfRaw;
          if (unwrapped && typeof unwrapped === "object") {
            setWf(unwrapped as WorkflowDetail);
          } else {
            setWf(null);
          }
        } else {
          setWf(null);
        }
      } catch {
        if (!cancelled) {
          setErr("Could not load recipe.");
          setRecipe(null);
          setCellDraft({});
          setWf(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recipeId]);

  const displayOps = useMemo(() => {
    const ops = (wf?.workflowoperations ?? []) as WfOp[];
    return buildDisplayWorkflowOps(ops);
  }, [wf]);

  const maxStepFromCells = useMemo(() => {
    let max = -1;
    for (const k of Object.keys(cellDraft)) {
      const m = /^(\d+)-/.exec(k);
      if (m) max = Math.max(max, Number(m[1]));
    }
    return max;
  }, [cellDraft]);

  const renderOps = useMemo((): WfOp[] => {
    if (displayOps.length) return displayOps;
    const n = maxStepFromCells + 1;
    if (n <= 0) return [];
    return Array.from({ length: n }, (_, i) => ({
      id: -(i + 1),
      processDefinitionName: `Step ${i + 1}`,
      mfgTechnologyName: "—",
      active: true as const,
    }));
  }, [displayOps, maxStepFromCells]);

  const stepParamGroups = useMemo(
    () => recipeGroupsFromPayloadLayouts(recipe?.payload?.stepLayouts),
    [recipe?.payload?.stepLayouts],
  );

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editBuf, setEditBuf] = useState({ t: "", n: "", x: "", c: false });

  useEffect(() => {
    if (!editingKey) return;
    const m = /^(\d+)-(.+)$/.exec(editingKey);
    if (!m) return;
    const si = Number(m[1]);
    const pk = m[2]!;
    setEditBuf({
      t: (cellDraft[`${si}-${pk}-t`] ?? "").trim(),
      n: (cellDraft[`${si}-${pk}-n`] ?? "").trim(),
      x: (cellDraft[`${si}-${pk}-x`] ?? "").trim(),
      c: isParamCriticalMarked(cellDraft, si, pk),
    });
  }, [editingKey, cellDraft]);

  const [wiRows, setWiRows] = useState<WiInstructionRow[]>([]);
  const [wiModal, setWiModal] = useState(false);
  const [wiSaving, setWiSaving] = useState(false);
  const [wiForm, setWiForm] = useState({
    title: "",
    description: "",
    safetyNotes: "",
    estimatedTime: "",
  });

  const [qsRows, setQsRows] = useState<QsRow[]>([]);
  const [qsModal, setQsModal] = useState(false);
  const [qsSaving, setQsSaving] = useState(false);
  const [qsForm, setQsForm] = useState({
    spec: "",
    method: "",
    frequency: "",
    acceptance: "",
  });

  const loadWorkInstructions = useCallback(async () => {
    const id = Number(recipeId);
    if (!Number.isFinite(id) || id <= 0) return;
    const rows = await getList(ENDPOINT.RECIPE_WORK_INSTRUCTIONS, { recipeId: id });
    setWiRows(Array.isArray(rows) ? (rows as WiInstructionRow[]) : []);
  }, [recipeId]);

  useEffect(() => {
    void loadWorkInstructions();
  }, [loadWorkInstructions]);

  const loadQualitySpecs = useCallback(async () => {
    const id = Number(recipeId);
    if (!Number.isFinite(id) || id <= 0) return;
    const rows = await getList(ENDPOINT.RECIPE_QUALITY_SPECS, { recipeId: id });
    setQsRows(Array.isArray(rows) ? (rows as QsRow[]) : []);
  }, [recipeId]);

  useEffect(() => {
    void loadQualitySpecs();
  }, [loadQualitySpecs]);

  const wfOpIds = useMemo(() => {
    const from = recipe?.payload?.workflowOperationIds;
    if (Array.isArray(from) && from.length) {
      return from.filter((x): x is number => typeof x === "number" && x > 0);
    }
    return renderOps.map((o) => Number(o.id)).filter((n) => Number.isFinite(n) && n > 0);
  }, [recipe?.payload?.workflowOperationIds, renderOps]);

  const persistRecipeCells = useCallback(
    async (nextCells: Record<string, string>) => {
      if (!recipe) return;
      const tnmErrs = validateRecipeTnmOrdering(nextCells);
      if (tnmErrs.length) {
        const shown = tnmErrs.slice(0, 2).join(" ");
        const more = tnmErrs.length > 2 ? ` (+${tnmErrs.length - 2} more)` : "";
        toast.error(`${shown}${more}`);
        return;
      }
      try {
        const gid = (recipe.materialGradeIds ?? []).filter(
          (x): x is number => typeof x === "number" && x > 0,
        );
        await postRecipe({
          id: recipe.id,
          workflowId: recipe.workflowId,
          name: recipe.name,
          materialCategoryId: recipe.materialCategoryId ?? null,
          materialGradeId: gid[0],
          materialGradeIds: gid.length ? gid : undefined,
          status: normalizeSaveStatus(recipe.status ?? "draft"),
          payload: {
            cells: nextCells,
            workflowOperationIds: wfOpIds,
            ...(recipe.payload?.submittedForReview === true
              ? { submittedForReview: true as const }
              : {}),
            ...(recipe.payload?.submittedForApproval === true
              ? { submittedForApproval: true as const }
              : {}),
          },
        });
        const raw = await getListById(ENDPOINT.RECIPE_LIST_BY_ID, recipe.id);
        if (raw && typeof raw === "object") {
          const r2 = raw as RecipeDetail;
          setRecipe(r2);
          setCellDraft({ ...((r2.payload?.cells ?? {}) as Record<string, string>) });
        }
        setEditingKey(null);
        toast.success("Saved.");
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not save recipe.");
      }
    },
    [recipe, wfOpIds],
  );

  const [submitReviewBusy, setSubmitReviewBusy] = useState(false);

  const submitForReview = useCallback(async () => {
    if (!recipe) return;
    const tnmErrs = validateRecipeTnmOrdering(cellDraft);
    if (tnmErrs.length) {
      const shown = tnmErrs.slice(0, 2).join(" ");
      const more = tnmErrs.length > 2 ? ` (+${tnmErrs.length - 2} more)` : "";
      toast.error(`${shown}${more}`);
      return;
    }
    setSubmitReviewBusy(true);
    try {
      const gid = (recipe.materialGradeIds ?? []).filter(
        (x): x is number => typeof x === "number" && x > 0,
      );
      await postRecipe({
        id: recipe.id,
        workflowId: recipe.workflowId,
        name: recipe.name,
        materialCategoryId: recipe.materialCategoryId ?? null,
        materialGradeId: gid[0],
        materialGradeIds: gid.length ? gid : undefined,
        status: "pending",
        payload: {
          cells: cellDraft,
          workflowOperationIds: wfOpIds,
          submittedForReview: true,
        },
      });
      const raw = await getListById(ENDPOINT.RECIPE_LIST_BY_ID, recipe.id);
      if (raw && typeof raw === "object") {
        const r2 = raw as RecipeDetail;
        setRecipe(r2);
        setCellDraft({ ...((r2.payload?.cells ?? {}) as Record<string, string>) });
      }
      toast.success("Submitted for review.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not submit.");
    } finally {
      setSubmitReviewBusy(false);
    }
  }, [recipe, wfOpIds, cellDraft]);

  const [submitApprovalBusy, setSubmitApprovalBusy] = useState(false);

  const submitForApproval = useCallback(async () => {
    if (!recipe) return;
    const tnmErrs = validateRecipeTnmOrdering(cellDraft);
    if (tnmErrs.length) {
      const shown = tnmErrs.slice(0, 2).join(" ");
      const more = tnmErrs.length > 2 ? ` (+${tnmErrs.length - 2} more)` : "";
      toast.error(`${shown}${more}`);
      return;
    }
    setSubmitApprovalBusy(true);
    try {
      const gid = (recipe.materialGradeIds ?? []).filter(
        (x): x is number => typeof x === "number" && x > 0,
      );
      await postRecipe({
        id: recipe.id,
        workflowId: recipe.workflowId,
        name: recipe.name,
        materialCategoryId: recipe.materialCategoryId ?? null,
        materialGradeId: gid[0],
        materialGradeIds: gid.length ? gid : undefined,
        status: "approved",
        payload: {
          cells: cellDraft,
          workflowOperationIds: wfOpIds,
          submittedForReview: true,
          submittedForApproval: true,
        },
      });
      const raw = await getListById(ENDPOINT.RECIPE_LIST_BY_ID, recipe.id);
      if (raw && typeof raw === "object") {
        const r2 = raw as RecipeDetail;
        setRecipe(r2);
        setCellDraft({ ...((r2.payload?.cells ?? {}) as Record<string, string>) });
      }
      toast.success("Recipe approved.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not approve recipe.");
    } finally {
      setSubmitApprovalBusy(false);
    }
  }, [recipe, wfOpIds, cellDraft]);

  const rawRecipeStatus = (recipe?.status ?? "draft").trim().toLowerCase();
  const recipeApprovalStatus =
    rawRecipeStatus === "in review" ||
    rawRecipeStatus === "in_review" ||
    rawRecipeStatus === "in-review"
      ? "pending"
      : rawRecipeStatus;
  /** All-green pipeline + banner only when workflow is fully approved in DB. */
  const isRecipeFullyApproved = recipeApprovalStatus === "approved";

  const approvalDate = isRecipeFullyApproved
    ? formatMesDate(recipe?.updatedate ?? recipe?.createdate)
    : "—";

  const subtitleLine = useMemo(() => {
    const techs = Array.from(
      new Set(renderOps.map((o) => (o.mfgTechnologyName ?? "").trim()).filter(Boolean)),
    );
    const grades = (recipe?.materialGradeNames ?? []).filter(Boolean).join(", ");
    const machines = Array.from(
      new Set(renderOps.map((o) => (o.machineName ?? "").trim()).filter(Boolean)),
    );
    const cat = (recipe?.materialCategoryName ?? "").trim();
    const bits: string[] = [];
    if (techs[0]) bits.push(techs[0]);
    if (grades) bits.push(grades);
    else if (cat) bits.push(cat);
    if (machines[0]) bits.push(machines[0]);
    return bits.join(" · ");
  }, [renderOps, recipe]);

  const paramCountLive = useMemo(() => {
    const keys = Object.keys(cellDraft).filter(
      (k) => (cellDraft[k] ?? "").trim() !== "" && !/-c$/.test(k),
    );
    return Math.max(0, Math.round(keys.length / 3));
  }, [cellDraft]);

  const criticalCountLive = useMemo(() => countCriticalTargets(cellDraft), [cellDraft]);

  const wfTitle = (wf?.workflowname ?? recipe?.workflowname ?? "").trim() || "—";

  const submittedForReview = useMemo(
    () => Boolean(recipe?.payload && recipe.payload.submittedForReview === true),
    [recipe?.payload],
  );

  const awaitingQualityApproval =
    recipeApprovalStatus === "pending" && !isRecipeFullyApproved;

  const showSubmitForReview =
    !isRecipeFullyApproved &&
    recipeApprovalStatus !== "pending" &&
    !submittedForReview;

  const showSubmitForApproval = awaitingQualityApproval;

  const moveWorkInstruction = useCallback(
    async (index: number, dir: -1 | 1) => {
      if (!recipe) return;
      const to = index + dir;
      if (to < 0 || to >= wiRows.length) return;
      const next = [...wiRows];
      const a = next[index]!;
      const b = next[to]!;
      next[index] = b;
      next[to] = a;
      const orderedIds = next.map((r) => r.id);
      try {
        await patchRecipeWorkInstructionReorder({ recipeId: recipe.id, orderedIds });
        setWiRows(next);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not reorder.");
      }
    },
    [recipe, wiRows],
  );

  const deleteWorkInstruction = useCallback(
    async (id: number) => {
      try {
        await deleteRecipeWorkInstruction(id);
        await loadWorkInstructions();
        toast.success("Removed.");
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not delete.");
      }
    },
    [loadWorkInstructions],
  );

  const deleteQualitySpec = useCallback(
    async (id: number) => {
      try {
        await deleteRecipeQualitySpecification(id);
        await loadQualitySpecs();
        toast.success("Specification removed.");
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not delete.");
      }
    },
    [loadQualitySpecs],
  );

  const submitWorkInstructionModal = useCallback(async () => {
    if (!recipe) return;
    const title = wiForm.title.trim();
    if (!title) {
      toast.error("Title is required.");
      return;
    }
    setWiSaving(true);
    try {
      await postRecipeWorkInstruction({
        recipeId: recipe.id,
        title,
        description: wiForm.description.trim(),
        safetyNotes: wiForm.safetyNotes.trim(),
        estimatedTime: wiForm.estimatedTime.trim(),
      });
      setWiModal(false);
      setWiForm({ title: "", description: "", safetyNotes: "", estimatedTime: "" });
      await loadWorkInstructions();
      toast.success("Work instruction added.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not add.");
    } finally {
      setWiSaving(false);
    }
  }, [recipe, wiForm, loadWorkInstructions]);

  const submitQualitySpecModal = useCallback(async () => {
    if (!recipe) return;
    const spec = qsForm.spec.trim();
    if (!spec) {
      toast.error("Spec is required.");
      return;
    }
    setQsSaving(true);
    try {
      await postRecipeQualitySpecification({
        recipeId: recipe.id,
        spec,
        method: qsForm.method.trim(),
        frequency: qsForm.frequency.trim(),
        acceptance: qsForm.acceptance.trim(),
      });
      setQsModal(false);
      setQsForm({ spec: "", method: "", frequency: "", acceptance: "" });
      await loadQualitySpecs();
      toast.success("Quality specification added.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not add specification.");
    } finally {
      setQsSaving(false);
    }
  }, [recipe, qsForm, loadQualitySpecs]);

  if (loading) {
    return (
        <>
        <div className="tv-mes-v2-page">
          <div className="tv-mes-v2-shell">
            <div className="tv-recipe-detail__loading" role="status">
              <Spinner animation="border" size="sm" />
              <span>Loading recipe…</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (err || !recipe) {
    return (
        <>
        <div className="tv-mes-v2-page">
          <div className="tv-mes-v2-shell">
            <div className="tv-mes-v2-panel">
              <p className="tv-mes-v2-page-desc">{err ?? "Recipe not found."}</p>
              <Link
                to="/mes-trurecipe/library"
                className="tv-mes-v2-btn-orange tv-mes-v2-btn-orange--mt"
              >
                Back to Recipe Library
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
      <>
      <div className="tv-mes-v2-page">
        <div className="tv-mes-v2-shell">
          <div className="tv-recipe-detail">
            <div className="tv-recipe-detail__head">
              <div className="tv-recipe-detail__head-main">
                <Link to="/mes-trurecipe/library" className="tv-recipe-detail__back">
                  <i className="fa fa-arrow-left" aria-hidden />
                  Back to Recipe Library
                </Link>
                {subtitleLine ? <p className="tv-recipe-detail__sub">{subtitleLine}</p> : null}
                <p className="tv-recipe-detail__meta">
                  Version 1.0 · {formatMesDate(recipe.updatedate ?? recipe.createdate)} by{" "}
                  {(recipe.createdby ?? "").trim() || "—"}
                </p>
              </div>
              <div className="tv-recipe-detail__head-aside">
                <div className="tv-recipe-detail__head-row">
                  <button type="button" className="tv-recipe-detail__ghost" disabled title="Coming soon">
                    <i className="fa fa-clone" aria-hidden />
                    Clone Recipe
                  </button>
                  <span
                    className={`tv-mes-v2-status tv-mes-v2-status--${
                      isRecipeFullyApproved
                        ? "approved"
                        : recipeApprovalStatus === "pending"
                          ? "pending"
                          : "draft"
                    }`}
                  >
                    {isRecipeFullyApproved
                      ? "Active"
                      : recipeApprovalStatus === "pending"
                        ? "Pending"
                        : "Draft"}
                  </span>
                </div>
                <Link
                  to="/mes-trurecipe/guided"
                  className="tv-recipe-detail__primary tv-recipe-detail__primary--stack"
                >
                  <i className="fa fa-eye" aria-hidden />
                  Open Guided View
                </Link>
              </div>
            </div>

            <div className="tv-mes-v2-panel tv-recipe-detail__surface">
              <p className="tv-recipe-detail__eyebrow">Recipe lineage</p>
              <div className="tv-recipe-detail__lineage">
                <div className="tv-recipe-detail__lineage-card">
                  <div className="tv-recipe-detail__lineage-label">Process definitions</div>
                  <ul className="tv-recipe-detail__lineage-list">
                    {renderOps.length ? (
                      renderOps.map((op, i) => (
                        <li key={op.id ?? i}>
                          <span className="tv-recipe-detail__lineage-li">
                            <i className="fa fa-cog" aria-hidden />
                            <span>{(op.processDefinitionName ?? "").trim() || "—"}</span>
                          </span>
                        </li>
                      ))
                    ) : (
                      <li>
                        <span className="tv-recipe-detail__lineage-li">
                          <i className="fa fa-cog" aria-hidden />
                          <span>—</span>
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
                <span className="tv-recipe-detail__arrow" aria-hidden>
                  →
                </span>
                <div className="tv-recipe-detail__lineage-card">
                  <div className="tv-recipe-detail__lineage-label">Workflow template</div>
                  <p className="tv-recipe-detail__lineage-one">
                    <i className="fa fa-sitemap" aria-hidden /> {wfTitle}
                  </p>
                </div>
                <span className="tv-recipe-detail__arrow" aria-hidden>
                  →
                </span>
                <div className="tv-recipe-detail__lineage-card tv-recipe-detail__lineage-card--accent">
                  <div className="tv-recipe-detail__lineage-label">This recipe</div>
                  <p className="tv-recipe-detail__lineage-one">
                    <i className="fa fa-file-text-o" aria-hidden /> {paramCountLive} params
                  </p>
                </div>
                <span className="tv-recipe-detail__arrow" aria-hidden>
                  →
                </span>
                <div className="tv-recipe-detail__lineage-card">
                  <div className="tv-recipe-detail__lineage-label">Applied to jobs</div>
                  <p className="tv-recipe-detail__lineage-one">
                    <i className="fa fa-key" aria-hidden /> Linked when jobs use this workflow
                  </p>
                </div>
              </div>
            </div>

            <div className="tv-mes-v2-panel tv-recipe-detail__approval-panel">
              <p className="tv-recipe-detail__eyebrow">Approval pipeline</p>
              <div className="tv-recipe-detail__steps-wrap">
                {isRecipeFullyApproved ? (
                  <div className="tv-recipe-detail__pipeline">
                    <div className="tv-recipe-detail__pipeline-track" aria-hidden />
                    <div className="tv-recipe-detail__pipeline-steps">
                      {(
                        [
                          { label: "Engineer Creates", ok: true },
                          { label: "Supervisor Reviews", ok: true },
                          { label: "Quality Approves", ok: true },
                        ] as const
                      ).map((s, i) => (
                        <div key={s.label} className="tv-recipe-detail__pipeline-item">
                          <div className="tv-recipe-detail__pipeline-icon-wrap">
                            <div
                              className={`tv-recipe-detail__pipeline-icon${s.ok ? " is-done" : ""}`}
                            >
                              {s.ok ? <i className="fa fa-check" aria-hidden /> : i + 1}
                            </div>
                          </div>
                          <div className="tv-recipe-detail__pipeline-label">{s.label}</div>
                          <div
                            className={`tv-recipe-detail__pipeline-state${s.ok ? " is-ok" : ""}`}
                          >
                            {s.ok ? (i === 0 ? "Completed" : "Approved") : "Pending"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="tv-recipe-detail__pipeline tv-recipe-detail__pipeline--draft">
                    <div className="tv-recipe-detail__pipeline-track" aria-hidden />
                    <div className="tv-recipe-detail__pipeline-steps">
                      <div className="tv-recipe-detail__pipeline-item">
                        <div className="tv-recipe-detail__pipeline-icon-wrap">
                          <div className="tv-recipe-detail__pipeline-icon is-done">
                            <i className="fa fa-check" aria-hidden />
                          </div>
                        </div>
                        <div className="tv-recipe-detail__pipeline-label">Engineer Creates</div>
                        <div className="tv-recipe-detail__pipeline-state is-ok">Completed</div>
                      </div>
                      <div className="tv-recipe-detail__pipeline-item">
                        <div className="tv-recipe-detail__pipeline-icon-wrap">
                          <div
                            className={`tv-recipe-detail__pipeline-icon${
                              awaitingQualityApproval ? " is-done" : " is-pending"
                            }`}
                          >
                            {awaitingQualityApproval ? (
                              <i className="fa fa-check" aria-hidden />
                            ) : (
                              <i className="fa fa-clock-o" aria-hidden />
                            )}
                          </div>
                        </div>
                        <div className="tv-recipe-detail__pipeline-label">Supervisor Reviews</div>
                        <div
                          className={`tv-recipe-detail__pipeline-state${
                            awaitingQualityApproval ? " is-ok" : " is-pending"
                          }`}
                        >
                          {awaitingQualityApproval
                            ? "Approved"
                            : submittedForReview
                              ? "In review"
                              : "Pending"}
                        </div>
                      </div>
                      <div className="tv-recipe-detail__pipeline-item">
                        <div className="tv-recipe-detail__pipeline-icon-wrap">
                          <div
                            className={`tv-recipe-detail__pipeline-icon${
                              awaitingQualityApproval ? " is-pending" : " is-wait"
                            }`}
                          >
                            <i className="fa fa-clock-o" aria-hidden />
                          </div>
                        </div>
                        <div className="tv-recipe-detail__pipeline-label">Quality Approves</div>
                        <div
                          className={`tv-recipe-detail__pipeline-state${
                            awaitingQualityApproval ? " is-pending" : " is-wait"
                          }`}
                        >
                          {awaitingQualityApproval ? "Pending" : "Waiting"}
                        </div>
                      </div>
                    </div>
                    {showSubmitForReview || showSubmitForApproval ? (
                      <div className="tv-recipe-detail__pipeline-actions">
                        <div aria-hidden />
                        <div className="tv-recipe-detail__pipeline-actions__mid">
                          {showSubmitForReview ? (
                            <Button
                              type="button"
                              variant="primary"
                              size="sm"
                              className="tv-recipe-detail__pipeline-submit-btn"
                              disabled={submitReviewBusy}
                              onClick={() => void submitForReview()}
                            >
                              {submitReviewBusy ? "Submitting…" : "Submit for Review"}
                            </Button>
                          ) : null}
                        </div>
                        <div className="tv-recipe-detail__pipeline-actions__end">
                          {showSubmitForApproval ? (
                            <Button
                              type="button"
                              variant="primary"
                              size="sm"
                              className="tv-recipe-detail__pipeline-submit-btn"
                              disabled={submitApprovalBusy}
                              onClick={() => void submitForApproval()}
                            >
                              {submitApprovalBusy ? "Submitting…" : "Submit for Approval"}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              {isRecipeFullyApproved ? (
                <p className="tv-recipe-detail__approved-banner tv-recipe-detail__approved-banner--inset">
                  Approved on {approvalDate}
                </p>
              ) : null}
            </div>

          {renderOps.length === 0 ? (
            <section className="tv-recipe-detail__section">
              <p className="tv-recipe-detail__empty">
                No workflow steps or saved parameters to display for this recipe.
              </p>
            </section>
          ) : (
            renderOps.map((op, sIdx) => {
            const proc = (op.processDefinitionName ?? "").trim() || "—";
            const tech = (op.mfgTechnologyName ?? "").trim() || "—";
            const groups = resolveRecipeGroupsForStep(stepParamGroups, sIdx, op);
            return (
              <section key={op.id ?? sIdx} className="tv-recipe-detail__section tv-recipe-detail__section--step">
                <h2 className="tv-recipe-detail__section-title">
                  Step {sIdx + 1}: {proc}
                  {tech !== "—" ? (
                    <span className="tv-recipe-detail__step-tech"> · {tech}</span>
                  ) : null}
                </h2>
                {groups.map((g) => (
                  <div key={g.title} className="tv-recipe-detail__param-section">
                    <h3 className="tv-recipe-detail__param-section-title">
                      {recipeGroupDisplayTitle(g.title)}
                    </h3>
                    <div className="tv-recipe-detail__param-grid">
                      {g.params.map((p: RecipeParamDef) => {
                        const ck = (slot: "t" | "n" | "x") => `${sIdx}-${p.key}-${slot}`;
                        const t = (cellDraft[ck("t")] ?? "").trim();
                        const n = (cellDraft[ck("n")] ?? "").trim();
                        const x = (cellDraft[ck("x")] ?? "").trim();
                        const unit = p.unit ?? "";
                        const range =
                          n || x
                            ? `${n || "—"} – ${x || "—"}${unit ? ` ${unit}` : ""}`
                            : `—${unit ? ` ${unit}` : ""}`;
                        const isCrit = isParamCriticalMarked(cellDraft, sIdx, p.key);
                        const canCrit = CRITICAL_RECIPE_PARAM_KEYS.has(p.key);
                        const ek = `${sIdx}-${p.key}`;
                        const isEdit = editingKey === ek;
                        const topAccent = isCrit
                          ? " tv-recipe-detail__param-card--top-red"
                          : p.key === "cp"
                            ? " tv-recipe-detail__param-card--top-blue"
                            : " tv-recipe-detail__param-card--top-neutral";
                        return (
                          <div
                            key={p.key}
                            className={`tv-recipe-detail__param-card${topAccent}${
                              isCrit ? " tv-recipe-detail__param-card--critical" : ""
                            }`}
                          >
                            <button
                              type="button"
                              className="tv-recipe-detail__param-edit"
                              title={isEdit ? "Close editor" : "Edit parameter"}
                              aria-label={isEdit ? "Close editor" : "Edit parameter"}
                              onClick={() => setEditingKey(isEdit ? null : ek)}
                            >
                              <i className="fa fa-pencil" aria-hidden />
                            </button>
                            <div className="tv-recipe-detail__param-card-head">
                              <span className="tv-recipe-detail__param-name">{p.label}</span>
                            </div>
                            {isEdit ? (
                              <div className="tv-recipe-detail__param-form">
                                <div>
                                  <span className="tv-recipe-detail__param-target-label">Target</span>
                                  <Form.Control
                                    size="sm"
                                    value={editBuf.t}
                                    onChange={(e) =>
                                      setEditBuf((b) => ({ ...b, t: e.target.value }))
                                    }
                                  />
                                </div>
                                <div className="tv-recipe-detail__tmm-read">
                                  <div>
                                    <span className="tv-recipe-detail__tmm-lbl">Nominal</span>
                                    <Form.Control
                                      size="sm"
                                      value={editBuf.n}
                                      onChange={(e) =>
                                        setEditBuf((b) => ({ ...b, n: e.target.value }))
                                      }
                                    />
                                  </div>
                                  <div>
                                    <span className="tv-recipe-detail__tmm-lbl">Tolerance</span>
                                    <Form.Control
                                      size="sm"
                                      value={editBuf.x}
                                      onChange={(e) =>
                                        setEditBuf((b) => ({ ...b, x: e.target.value }))
                                      }
                                    />
                                  </div>
                                </div>
                                {canCrit ? (
                                  <label className="tv-recipe-detail__param-check">
                                    <input
                                      type="checkbox"
                                      checked={editBuf.c}
                                      onChange={(e) =>
                                        setEditBuf((b) => ({ ...b, c: e.target.checked }))
                                      }
                                    />
                                    Critical
                                  </label>
                                ) : null}
                                <div className="tv-recipe-detail__param-actions">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="primary"
                                    onClick={() => {
                                      const next = { ...cellDraft };
                                      next[ck("t")] = editBuf.t;
                                      next[ck("n")] = editBuf.n;
                                      next[ck("x")] = editBuf.x;
                                      if (canCrit) {
                                        next[`${sIdx}-${p.key}-c`] = editBuf.c ? "1" : "0";
                                      } else {
                                        delete next[`${sIdx}-${p.key}-c`];
                                      }
                                      void persistRecipeCells(next);
                                    }}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="light"
                                    onClick={() => setEditingKey(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="tv-recipe-detail__param-target">
                                  <span className="tv-recipe-detail__param-target-label">Target</span>
                                  <span className="tv-recipe-detail__param-target-val">
                                    {t || "—"}
                                    {unit ? (
                                      <span className="tv-recipe-detail__unit"> {unit}</span>
                                    ) : null}
                                  </span>
                                </div>
                                <div className="tv-recipe-detail__tmm-read">
                                  <div>
                                    <span className="tv-recipe-detail__tmm-lbl">Nominal</span>
                                    <span className="tv-recipe-detail__tmm-val">
                                      {n || "—"}
                                      {unit ? ` ${unit}` : ""}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="tv-recipe-detail__tmm-lbl">Tolerance</span>
                                    <span className="tv-recipe-detail__tmm-val">
                                      {x || "—"}
                                      {unit ? ` ${unit}` : ""}
                                    </span>
                                  </div>
                                </div>
                                <p className="tv-recipe-detail__param-meta">Range: {range}</p>
                                <p className="tv-recipe-detail__param-meta">
                                  Source: {paramSourceLabel(p.key)}
                                </p>
                                {isCrit ? (
                                  <span className="tv-recipe-detail__critical">Critical</span>
                                ) : null}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </section>
            );
          })
          )}

          <section className="tv-recipe-detail__section">
            <div className="tv-mes-v2-panel">
              <div className="tv-recipe-detail__split-head">
                <h2 className="tv-recipe-detail__section-title">
                  <i className="fa fa-book" aria-hidden /> Work instructions
                </h2>
                <button
                  type="button"
                  className="tv-recipe-detail__btn-add"
                  onClick={() => setWiModal(true)}
                >
                  + Add instruction
                </button>
              </div>
              {wiRows.length === 0 ? (
                <p className="tv-recipe-detail__empty">No work instructions yet.</p>
              ) : (
                <div className="tv-recipe-detail__wi-list">
                  {wiRows.map((row, idx) => {
                    const primary =
                      [row.title?.trim(), row.estimatedTime?.trim()].filter(Boolean).join(" — ") ||
                      "(untitled)";
                    return (
                      <div key={row.id} className="tv-recipe-detail__wi-row">
                        <div className="tv-recipe-detail__wi-badges">
                          <span className="tv-recipe-detail__wi-dot" aria-hidden />
                          <i className="fa fa-shield tv-recipe-detail__wi-shield" aria-hidden />
                        </div>
                        <div className="tv-recipe-detail__wi-main">{primary}</div>
                        <div className="tv-recipe-detail__wi-actions">
                          <button
                            type="button"
                            className="tv-recipe-detail__wi-icon-btn"
                            disabled={idx === 0}
                            title="Move up"
                            aria-label="Move up"
                            onClick={() => void moveWorkInstruction(idx, -1)}
                          >
                            <i className="fa fa-arrow-up" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="tv-recipe-detail__wi-icon-btn"
                            disabled={idx === wiRows.length - 1}
                            title="Move down"
                            aria-label="Move down"
                            onClick={() => void moveWorkInstruction(idx, 1)}
                          >
                            <i className="fa fa-arrow-down" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="tv-recipe-detail__wi-icon-btn tv-recipe-detail__wi-icon-btn--danger"
                            title="Delete"
                            aria-label="Delete"
                            onClick={() => void deleteWorkInstruction(row.id)}
                          >
                            <i className="fa fa-trash" aria-hidden />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section className="tv-recipe-detail__section">
            <div className="tv-mes-v2-panel">
              <div className="tv-recipe-detail__split-head">
                <h2 className="tv-recipe-detail__section-title">
                  <i className="fa fa-cog" aria-hidden /> Quality specifications
                </h2>
                <button
                  type="button"
                  className="tv-recipe-detail__btn-add"
                  onClick={() => setQsModal(true)}
                >
                  + Add Spec
                </button>
              </div>
              <div className="tv-recipe-detail__table-wrap">
                <table className="tv-recipe-detail__table">
                  <thead>
                    <tr>
                      <th>Spec</th>
                      <th>Method</th>
                      <th>Frequency</th>
                      <th>Acceptance</th>
                      <th className="tv-recipe-detail__table-col-actions" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {qsRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="tv-recipe-detail__table-empty">
                          No quality specifications yet.
                        </td>
                      </tr>
                    ) : (
                      qsRows.map((row) => (
                        <tr key={row.id}>
                          <td>{(row.spec ?? "").trim() || "—"}</td>
                          <td>{(row.method ?? "").trim() || "—"}</td>
                          <td>{(row.frequency ?? "").trim() || "—"}</td>
                          <td>{(row.acceptance ?? "").trim() || "—"}</td>
                          <td className="tv-recipe-detail__table-actions">
                            <button
                              type="button"
                              className="tv-recipe-detail__wi-icon-btn tv-recipe-detail__wi-icon-btn--danger"
                              title="Delete"
                              aria-label="Delete"
                              onClick={() => void deleteQualitySpec(row.id)}
                            >
                              <i className="fa fa-trash" aria-hidden />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <p className="tv-recipe-detail__summary">
            {paramCountLive} parameters · {criticalCountLive} critical targets (from recipe data)
          </p>
          </div>
        </div>
      </div>

      <Modal
        show={wiModal}
        onHide={() => !wiSaving && setWiModal(false)}
        centered
        backdrop="static"
        keyboard={!wiSaving}
        dialogClassName="tv-recipe-form-modal"
        contentClassName="tv-recipe-form-modal__content"
      >
        <Modal.Header closeButton={false} className="tv-recipe-form-modal__header">
          <Modal.Title as="h5" className="tv-recipe-form-modal__title">
            Add Work Instruction
          </Modal.Title>
          <button
            type="button"
            className="tv-recipe-form-modal__close"
            aria-label="Close"
            disabled={wiSaving}
            onClick={() => setWiModal(false)}
          >
            <i className="fa fa-times" aria-hidden />
          </button>
        </Modal.Header>
        <Modal.Body className="tv-recipe-form-modal__body">
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              placeholder="Step title"
              value={wiForm.title}
              onChange={(e) => setWiForm((f) => ({ ...f, title: e.target.value }))}
              disabled={wiSaving}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Detailed instructions"
              value={wiForm.description}
              onChange={(e) => setWiForm((f) => ({ ...f, description: e.target.value }))}
              disabled={wiSaving}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Safety Notes</Form.Label>
            <Form.Control
              placeholder="PPE, warnings, etc."
              value={wiForm.safetyNotes}
              onChange={(e) => setWiForm((f) => ({ ...f, safetyNotes: e.target.value }))}
              disabled={wiSaving}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Estimated Time</Form.Label>
            <Form.Control
              placeholder="e.g., 15 min"
              value={wiForm.estimatedTime}
              onChange={(e) => setWiForm((f) => ({ ...f, estimatedTime: e.target.value }))}
              disabled={wiSaving}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="tv-recipe-form-modal__footer">
          <button
            type="button"
            className="tv-recipe-form-modal__btn-cancel"
            onClick={() => setWiModal(false)}
            disabled={wiSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tv-recipe-form-modal__btn-submit"
            onClick={() => void submitWorkInstructionModal()}
            disabled={wiSaving}
          >
            {wiSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" role="status" />
                Saving…
              </>
            ) : (
              "Add"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={qsModal}
        onHide={() => !qsSaving && setQsModal(false)}
        centered
        backdrop="static"
        keyboard={!qsSaving}
        dialogClassName="tv-recipe-form-modal"
        contentClassName="tv-recipe-form-modal__content"
      >
        <Modal.Header closeButton={false} className="tv-recipe-form-modal__header">
          <Modal.Title as="h5" className="tv-recipe-form-modal__title">
            Add quality specification
          </Modal.Title>
          <button
            type="button"
            className="tv-recipe-form-modal__close"
            aria-label="Close"
            disabled={qsSaving}
            onClick={() => setQsModal(false)}
          >
            <i className="fa fa-times" aria-hidden />
          </button>
        </Modal.Header>
        <Modal.Body className="tv-recipe-form-modal__body">
          <Form.Group className="mb-3">
            <Form.Label>Spec</Form.Label>
            <Form.Control
              placeholder="e.g. Dimensional tolerance"
              value={qsForm.spec}
              onChange={(e) => setQsForm((f) => ({ ...f, spec: e.target.value }))}
              disabled={qsSaving}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Method</Form.Label>
            <Form.Control
              placeholder="e.g. CMM"
              value={qsForm.method}
              onChange={(e) => setQsForm((f) => ({ ...f, method: e.target.value }))}
              disabled={qsSaving}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Frequency</Form.Label>
            <Form.Control
              placeholder="e.g. Every lot"
              value={qsForm.frequency}
              onChange={(e) => setQsForm((f) => ({ ...f, frequency: e.target.value }))}
              disabled={qsSaving}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Acceptance</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Acceptance criteria"
              value={qsForm.acceptance}
              onChange={(e) => setQsForm((f) => ({ ...f, acceptance: e.target.value }))}
              disabled={qsSaving}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="tv-recipe-form-modal__footer">
          <button
            type="button"
            className="tv-recipe-form-modal__btn-cancel"
            onClick={() => setQsModal(false)}
            disabled={qsSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tv-recipe-form-modal__btn-submit"
            onClick={() => void submitQualitySpecModal()}
            disabled={qsSaving}
          >
            {qsSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" role="status" />
                Saving…
              </>
            ) : (
              "Add"
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
